/**
 * MechanismViewer — el visor de mecanismo, genérico y parametrizado por esquema.
 *
 * UNA pantalla para todos los mecanismos, no una por mecanismo. No sabe qué
 * gobierna: sabe manejar «fichas gobernadas» —cosas que alguien redacta, alguien
 * aprueba, quedan con historial y se pueden auditar—. Qué se lista, por qué se
 * filtra, qué columnas hay, qué secciones tiene el detalle, qué transiciones
 * existen y qué acciones se ofrecen sale del DESCRIPTOR que sirve el kernel.
 *
 * Renderizador fino (R-PT-01): sin lógica de negocio, sin IAM y sin routing
 * propio. Todo lo que decide aquí es cosmético —el kernel revalida el scope en
 * cada petición— y **ninguna validación semántica vive en el front**: la
 * propuesta viaja al kernel y su código de error se pinta tal cual.
 *
 * Escritura: se PROPONE (`POST /v1/mecanismos/{codigo}/acciones`) y se aprueba
 * en la puerta de siempre (`POST /v1/actions/{id}/approve`), reutilizando la
 * tarjeta de previsualización del resto de la aplicación. Cero contrato nuevo.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Search, ChevronLeft, ChevronRight, History, ClipboardList, Plus, Link as LinkIcon } from 'lucide-react'
import {
  getMecanismoEsquema,
  listMecanismoObjetos,
  getMecanismoObjeto,
  getMecanismoVersiones,
  getMecanismoRegistros,
  proposeMecanismoAction,
  getAction,
} from '../api/client.js'
import ActionPreviewEvent from '../components/events/ActionPreviewEvent.jsx'
import { AyudaValor, EditorCampo, ValorCampo } from './campos.jsx'
import { codigoDeVista, panelDe } from './registro.js'

// Códigos del envelope del kernel → mensaje de UI. Estable frente a cambios de
// texto del backend; sin match, cae al mensaje del propio error.
const ERRORES = {
  forbidden:               'No tienes permiso para esta operación.',
  mecanismo_no_encontrado: 'Ese mecanismo no está instalado.',
  mecanismo_no_disponible: 'El mecanismo no responde ahora mismo. No se pueden hacer cambios.',
  mecanismo_sin_adaptador: 'Este mecanismo todavía no admite escritura.',
  proyeccion_no_declarada: 'Este mecanismo no publica todavía esta información.',
  objeto_no_encontrado:    'No se ha encontrado.',
  campo_no_declarado:      'Hay campos que este mecanismo no admite.',
  accion_no_declarada:     'Esa acción no está disponible en este mecanismo.',
  filtro_no_declarado:     'Ese filtro no existe.',
  valor_no_declarado:      'Ese valor no es válido para el filtro.',
  parametros_invalidos:    'Faltan datos obligatorios.',
  // GEN-76·346 — el enlace de una acción caducada, ajena o inexistente: los
  // tres responden el mismo 404 del kernel (§5.4), y aquí se pintan igual a
  // propósito — no se le cuenta a quien abre el enlace cuál de los tres fue.
  accion_no_encontrada:    'Ese enlace ya no lleva a ninguna acción pendiente (puede haber expirado).',
}

// GEN-76·346 (D-1 de Werner) — nombre del parámetro de consulta que lleva el
// `action_id` en el enlace compartible. Vive aquí, no en `api/client.js`: es
// el visor de mecanismo quien decide su propia URL, el cliente API solo sabe
// pedir una acción por id.
const PARAM_ACCION = 'accion'
const msg = err => ERRORES[err?.code] ?? err?.message ?? 'Ha ocurrido un error inesperado.'

const TAM_PAGINA = 25

// GEN-GR-003 — estados que el listado NO muestra con el filtro de estado en
// «Todos»; solo se ven eligiéndolos en el filtro. El kernel no sabe pedir «todo
// menos X» (filtros de igualdad, un valor por eje), así que con «Todos» se pide
// cada estado visible por separado, se juntan, se ordenan por clave y se pagina
// aquí. Un mecanismo sin ninguno de estos estados se lista como siempre.
// Deuda: pasar a descriptor (ocultos_por_defecto) · GEN-GR-006
const OCULTOS_POR_DEFECTO = ['retirado']

// Tope de `tam` que acepta el kernel (`PAGINA_MAXIMA` de `mecanismos_api.py`).
// Un estado que lo alcanza puede traer filas de menos: se avisa, no se oculta.
const PAGINA_MAXIMA = 200

// Etiquetas de las columnas del METAMODELO (las que el visor conoce sin saber
// qué significan). No son campos del mecanismo: si no estuvieran aquí, la
// cabecera mostraría el identificador crudo.
const ETIQUETAS_META = {
  clave: 'Clave',
  titulo: 'Título',
  subtitulo: 'Subtítulo',
  estado: 'Estado',
  version: 'Versión',
  actualizado_en: 'Actualizado',
}

// Cabeceras de la pestaña de versiones. Estas columnas NO las declara el
// descriptor: las fija el genérico, igual que las del metamodelo, así que sus
// etiquetas viven aquí y no en el esquema del mecanismo.
const ETIQUETAS_VERSIONES = {
  version: 'Versión',
  es_vigente: 'Vigente',
  autor: 'Autor',
  aprobador: 'Autorizó',
  fecha: 'Fecha',
  motivo: 'Motivo',
}

// R-18 (GEN-76·135) — «que conste, no que se dé por vista».
//
// `aprobador` es quién AUTORIZÓ esa escritura concreta, y es la mitad de la
// traza doble que se quedaba en la API sin llegar al píxel. Pintarlo no basta:
// hay que pintar bien su HUECO. Un `null` aquí no significa «no aplica», que es
// lo que dice el guion largo con el que el resto de columnas rellenan su vacío;
// significa que NADIE registró quién autorizó ese cambio, y eso es justo lo que
// R-18 existe para que se vea. La proyección ya se niega a rellenarlo con un
// `coalesce` por la misma razón; sería absurdo deshacerlo en la última capa.
//
// 🔴 GEN-76·251 — «COLUMNA AUSENTE» Y «VALOR NULO» NO SON EL MISMO HUECO, y
// distinguirlos es la condición con la que #182 se aprobó.
//
// El API del kernel es pass-through: `_componer_select` hace `SELECT *` y
// `_filas` construye cada diccionario desde `cur.description`. ⇒ `aprobador`
// llega al píxel si y sólo si la vista lo declara. Medido sobre los dos
// artefactos A en disco: `_260805_1815.sql` declara la columna 0 veces y
// `_260901_2221.sql` 1 vez (`h.snapshot ->> 'aprobador' AS aprobador`), y el que
// la declara es el VIGENTE y NO está aplicado. ⇒ hoy, contra el backend real, la
// clave no viene en la fila y pintar «sin registrar» afirmaría «nadie registró
// quién autorizó esta escritura», que es FALSO: la verdad es que no se pregunta.
//
// Un hueco que dice «nadie lo registró» cuando la verdad es «no se pregunta» es
// el mismo defecto de R-18 una capa más tarde. El discriminador es `in` y no el
// valor: una clave ausente es lo único que separa los dos casos.
function celdaVersion(columna, fila) {
  if (columna !== 'aprobador') return undefined
  if (!(columna in fila)) {
    return (
      <span
        className="mec-hueco"
        title="La proyección de versiones todavía no expone la columna «aprobador». No es que nadie la registrara: es que no se pregunta (R-18)"
      >
        la proyección no publica este campo todavía
      </span>
    )
  }
  const v = fila.aprobador
  if (v === null || v === undefined || v === '') {
    return <span className="mec-hueco" title="Nadie registró quién autorizó esta escritura (R-18)">sin registrar</span>
  }
  return String(v)
}

/**
 * Un filtro declarado, listo para pintar: `{ etiqueta, valores }`, o `null` si
 * ese nombre no es filtrable en este esquema.
 *
 * GEN-76·133 (decisión (b) de Werner): `estado` es filtro de PRIMERA CLASE y su
 * enumerado sale del CICLO DE VIDA, no de `clasificacion`. Hasta esta versión
 * este componente buscaba TODO filtro en `clasificacion` y devolvía `null` si no
 * lo encontraba ⇒ un `estado` declarado por el kernel se habría pintado como
 * nada: el listado lo habría aceptado y el usuario no habría visto el control.
 * El front no inventa filtros ni valores; los resuelve como el kernel.
 */
function filtroDeclarado(esquema, id) {
  if (id === 'estado') {
    const valores = esquema.ciclo_de_vida?.estados ?? []
    return valores.length ? { etiqueta: ETIQUETAS_META.estado, valores } : null
  }
  const eje = (esquema.clasificacion ?? []).find(e => e.id === id)
  return eje ? { etiqueta: eje.etiqueta, valores: eje.valores ?? [] } : null
}

/** Valor de una columna del listado: campo declarado, eje o metadato. */
function celda(fila, columna, campos) {
  const campo = campos.find(c => c.id === columna)
  return campo ? <ValorCampo campo={campo} valor={fila[columna]} /> : <span>{fila[columna] ?? '—'}</span>
}

const SIN_FILTROS = {}

export default function MechanismViewer({ viewId, filtrosIniciales = SIN_FILTROS }) {
  const codigo = codigoDeVista(viewId)

  const [esquema, setEsquema]   = useState(null)
  const [error, setError]       = useState(null)
  const [cargando, setCargando] = useState(true)

  const [filtros, setFiltros] = useState(filtrosIniciales)
  const [q, setQ]             = useState('')
  const [pagina, setPagina]   = useState(1)
  const [listado, setListado] = useState({ objetos: [], total: 0 })

  const [clave, setClave]     = useState(null)
  const [detalle, setDetalle] = useState(null)
  const [pestana, setPestana] = useState('detalle')
  const [panelDatos, setPanelDatos] = useState(null)

  const [accionAbierta, setAccionAbierta] = useState(null)
  // GEN-GR-002 — «Duplicar»: el objeto de origen con el que se rellena el alta.
  // `null` = alta en blanco.
  const [duplicarDe, setDuplicarDe]       = useState(null)
  const [preview, setPreview]             = useState(null)
  const [enlaceCopiado, setEnlaceCopiado] = useState(false)
  // Estado PROPIO, no el `error` general: `cargarListado` resetea `error` a
  // `null` cada vez que recarga el listado (línea más abajo) — algo que
  // ocurre de forma rutinaria justo después de que el esquema termine de
  // cargar. Medido: compartir `error` hacía que ese reseteo, ganando la
  // carrera de microtareas, se comiera el mensaje de un enlace caducado
  // antes de que nadie llegara a verlo.
  const [accionError, setAccionError]     = useState(null)

  // GEN-76·346 (D-1 de Werner) — el enlace compartible, mitad de vuelta: al
  // MONTAR el visor, si la URL trae `?accion=<id>`, se rehidrata la MISMA
  // tarjeta que ve el proponente, con el GET nuevo — no una tarjeta distinta.
  // Se comprueba UNA vez, al montar (no en cada cambio de `codigo`): es la
  // lectura de un enlace que alguien acaba de abrir, no un estado que deba
  // sobrevivir a la navegación interna del propio visor.
  //
  // LO QUE ESTO NO RESUELVE, medido y no supuesto: si quien abre el enlace no
  // tiene YA este mecanismo como vista activa, este efecto no llega a
  // ejecutarse — `AppShell.jsx` decide la vista activa con un `useState`
  // fijo a `'chat'` y no lee la URL en absoluto (medido: sin `react-router`,
  // sin sincronización de `history`/`search` en todo el repositorio salvo la
  // limpieza del callback de Cognito). Un enlace abierto en una pestaña nueva
  // no aterriza aquí solo; hace falta que la vista de este mecanismo YA esté
  // activa (p. ej. la misma pestaña, recargada). Cerrar esa distancia es una
  // decisión de enrutado que esta ficha no cubre — ver el PARTE de entrega.
  useEffect(() => {
    const accionId = new URLSearchParams(window.location.search).get(PARAM_ACCION)
    if (!accionId) return
    let vivo = true
    getAction(accionId)
      .then(p => { if (vivo) setPreview(p) })
      .catch(e => { if (vivo) setAccionError(msg(e)) })
    return () => { vivo = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function copiarEnlace(actionId) {
    const url = `${window.location.origin}${window.location.pathname}?${PARAM_ACCION}=${encodeURIComponent(actionId)}`
    try {
      await navigator.clipboard.writeText(url)
      setEnlaceCopiado(true)
      setTimeout(() => setEnlaceCopiado(false), 2000)
    } catch {
      // Portapapeles no disponible (permiso denegado, contexto no seguro):
      // no es un error de la acción, así que no se pinta como tal. El enlace
      // sigue siendo válido; quien lo necesite lo compone a mano.
    }
  }

  // Esquema: una vez por mecanismo. Cambiar de mecanismo reinicia la pantalla
  // entera — nada de estado del anterior sobrevive.
  useEffect(() => {
    let vivo = true
    setEsquema(null); setError(null); setCargando(true)
    setClave(null); setDetalle(null); setFiltros(filtrosIniciales); setQ(''); setPagina(1)
    getMecanismoEsquema(codigo)
      .then(e => { if (vivo) setEsquema(e) })
      .catch(e => { if (vivo) setError(msg(e)) })
      .finally(() => { if (vivo) setCargando(false) })
    return () => { vivo = false }
  // `filtrosIniciales` solo se lee al montar o cambiar de mecanismo (el padre remonta con `key` para cambiarlos).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo])

  // Solo la ÚLTIMA petición de listado escribe en pantalla: una respuesta que
  // llega tarde (p. ej. la carga «Todos», que son varias peticiones, tras haber
  // elegido ya otro filtro) se descarta en vez de pisar a la buena.
  const ultimaPeticionListado = useRef(0)

  const cargarListado = useCallback(async () => {
    if (!esquema) return
    const peticion = ++ultimaPeticionListado.current
    const vigente = () => peticion === ultimaPeticionListado.current
    try {
      setError(null)
      const estados = esquema.ciclo_de_vida?.estados ?? []
      const ocultaEstados = !filtros.estado && estados.some(e => OCULTOS_POR_DEFECTO.includes(e))
      if (!ocultaEstados) {
        const datos = await listMecanismoObjetos(codigo, { q, filtros, pagina, tam: TAM_PAGINA })
        if (!vigente()) return
        setListado({ objetos: datos.objetos ?? [], total: datos.total ?? 0 })
        return
      }
      const visibles = estados.filter(e => !OCULTOS_POR_DEFECTO.includes(e))
      const respuestas = await Promise.all(visibles.map(estado =>
        listMecanismoObjetos(codigo, { q, filtros: { ...filtros, estado }, pagina: 1, tam: PAGINA_MAXIMA })))
      if (!vigente()) return
      const campoClave = esquema.objeto?.clave
      const todos = respuestas
        .flatMap(r => r.objetos ?? [])
        .sort((a, b) => String(a[campoClave] ?? '').localeCompare(String(b[campoClave] ?? '')))
      const desde = (pagina - 1) * TAM_PAGINA
      setListado({
        objetos: todos.slice(desde, desde + TAM_PAGINA),
        total: todos.length,
        truncado: respuestas.some(r => (r.objetos ?? []).length >= PAGINA_MAXIMA),
      })
    } catch (e) {
      if (!vigente()) return
      setError(msg(e))
      setListado({ objetos: [], total: 0 })
    }
  }, [codigo, esquema, q, filtros, pagina])

  useEffect(() => { cargarListado() }, [cargarListado])

  // Detalle + pestaña activa. Cada pestaña pide lo suyo; el kernel resuelve el
  // detalle y las métricas en una sola conexión.
  useEffect(() => {
    let vivo = true
    if (!clave) { setDetalle(null); return }
    getMecanismoObjeto(codigo, clave)
      .then(d => { if (vivo) setDetalle(d) })
      .catch(e => { if (vivo) setError(msg(e)) })
    return () => { vivo = false }
  }, [codigo, clave])

  useEffect(() => {
    let vivo = true
    setPanelDatos(null)
    if (!clave || pestana === 'detalle') return
    const peticion = pestana === 'versiones'
      ? getMecanismoVersiones(codigo, clave)
      : getMecanismoRegistros(codigo, clave, pestana)
    peticion
      .then(d => { if (vivo) setPanelDatos(d) })
      .catch(e => { if (vivo) setPanelDatos({ error: msg(e) }) })
    return () => { vivo = false }
  }, [codigo, clave, pestana])

  const campos = esquema?.campos ?? []
  const paginas = Math.max(1, Math.ceil((listado.total || 0) / TAM_PAGINA))

  // Acciones ofrecidas: las declaradas por el mecanismo. Una acción de alta se
  // ofrece siempre; las de objeto, solo con un objeto seleccionado.
  const acciones = useMemo(() => (esquema?.acciones ?? []), [esquema])

  async function proponer(accion, params) {
    try {
      setError(null)
      const p = await proposeMecanismoAction(codigo, accion.id, params)
      setPreview(p)
      setAccionAbierta(null)
      setDuplicarDe(null)
    } catch (e) {
      setError(msg(e))
    }
  }

  if (cargando) return <div className="mec-viewer mec-viewer--cargando">Cargando…</div>
  if (!esquema) {
    return (
      <div className="mec-viewer">
        <p className="mec-error" role="alert">{error ?? 'No se ha podido cargar el mecanismo.'}</p>
      </div>
    )
  }

  const etiquetaPlural = esquema.objeto?.etiqueta_plural ?? 'objetos'

  return (
    <div className="mec-viewer">
      <header className="mec-viewer__head">
        <h2 className="mec-viewer__titulo">{esquema.mecanismo?.nombre}</h2>
        <span className="mec-viewer__sub">{etiquetaPlural}</span>
      </header>

      {error && <p className="mec-error" role="alert">{error}</p>}
      {accionError && <p className="mec-error" role="alert">{accionError}</p>}

      <div className="mec-barra">
        <label className="mec-busqueda">
          <Search size={15} strokeWidth={1.8} aria-hidden="true" />
          <input
            type="search"
            placeholder={`Buscar en ${etiquetaPlural}…`}
            aria-label="Buscar"
            value={q}
            onChange={e => { setPagina(1); setQ(e.target.value) }}
          />
        </label>

        {/* Filtros: SOLO los declarados como filtrables, con sus valores
            cerrados. El front no inventa filtros ni valores. */}
        {(esquema.listado?.filtros ?? []).map(id => {
          const filtro = filtroDeclarado(esquema, id)
          if (!filtro) return null
          return (
            <label key={id} className="mec-filtro">
              <span>{filtro.etiqueta}</span>
              <select
                value={filtros[id] ?? ''}
                onChange={e => {
                  const v = e.target.value
                  setPagina(1)
                  setFiltros(f => (v ? { ...f, [id]: v } : Object.fromEntries(
                    Object.entries(f).filter(([k]) => k !== id))))
                }}
              >
                <option value="">Todos</option>
                {filtro.valores.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
          )
        })}

        {acciones.filter(a => a.tipo === 'alta').map(a => (
          <button key={a.id} className="mec-btn mec-btn--primary" onClick={() => { setDuplicarDe(null); setAccionAbierta(a) }}>
            <Plus size={15} strokeWidth={1.8} aria-hidden="true" /> {a.etiqueta}
          </button>
        ))}
      </div>

      <div className="mec-cuerpo">
        {listado.truncado && (
          <p className="mec-aviso" role="status">
            Algún estado tiene más de {PAGINA_MAXIMA} {etiquetaPlural} y no se muestran todos.
            Elige un estado en el filtro para verlos completos.
          </p>
        )}
        <table className="mec-tabla">
          <thead>
            <tr>
              {(esquema.listado?.columnas ?? []).map(c => (
                <th key={c} scope="col">
                  {campos.find(f => f.id === c)?.etiqueta
                    ?? (esquema.clasificacion ?? []).find(e => e.id === c)?.etiqueta
                    ?? ETIQUETAS_META[c]
                    ?? c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {listado.objetos.map(fila => {
              const k = fila[esquema.objeto.clave]
              return (
                <tr
                  key={k}
                  className={k === clave ? 'mec-fila mec-fila--activa' : 'mec-fila'}
                  onClick={() => { setClave(k); setPestana('detalle') }}
                >
                  {(esquema.listado?.columnas ?? []).map(c => (
                    <td key={c}>{celda(fila, c, campos)}</td>
                  ))}
                </tr>
              )
            })}
            {listado.objetos.length === 0 && (
              <tr><td colSpan={(esquema.listado?.columnas ?? []).length}>Sin resultados.</td></tr>
            )}
          </tbody>
        </table>

        <nav className="mec-paginacion" aria-label="Paginación">
          <button disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)} aria-label="Anterior">
            <ChevronLeft size={15} strokeWidth={1.8} aria-hidden="true" />
          </button>
          <span>{pagina} / {paginas}</span>
          <button disabled={pagina >= paginas} onClick={() => setPagina(p => p + 1)} aria-label="Siguiente">
            <ChevronRight size={15} strokeWidth={1.8} aria-hidden="true" />
          </button>
        </nav>
      </div>

      {clave && detalle && (
        <section className="mec-detalle" aria-label="Detalle">
          <nav className="mec-pestanas">
            <button
              className={pestana === 'detalle' ? 'activa' : ''}
              onClick={() => setPestana('detalle')}
            >Detalle</button>
            {esquema.proyecciones?.versiones && (
              <button
                className={pestana === 'versiones' ? 'activa' : ''}
                onClick={() => setPestana('versiones')}
              ><History size={14} strokeWidth={1.8} aria-hidden="true" /> Versiones</button>
            )}
            {(esquema.registros_ligados ?? []).map(r => (
              <button
                key={r.id}
                className={pestana === r.id ? 'activa' : ''}
                onClick={() => setPestana(r.id)}
              ><ClipboardList size={14} strokeWidth={1.8} aria-hidden="true" /> {r.etiqueta}</button>
            ))}
          </nav>

          {pestana === 'detalle' && (
            <>
              {(esquema.detalle?.secciones ?? []).map(s => {
                const Panel = panelDe(s.widget)
                return (
                  <div key={s.id} className="mec-seccion">
                    <h3>{s.titulo}</h3>
                    {Panel
                      ? <Panel objeto={detalle.objeto} descriptor={esquema} onProponer={proponer} />
                      : (s.campos ?? []).map(id => {
                          const campo = campos.find(c => c.id === id)
                          return (
                            <div key={id} className="mec-campo">
                              <span className="mec-campo__etiqueta">{campo?.etiqueta ?? id}</span>
                              <ValorCampo campo={campo} valor={detalle.objeto?.[id]} />
                            </div>
                          )
                        })}
                  </div>
                )
              })}

              {/* Tarjetas de métricas: el visor las PINTA; los números los da la
                  proyección del mecanismo. No agrega ni calcula nada. */}
              {(esquema.metricas?.tarjetas ?? []).length > 0 && (
                <div className="mec-metricas">
                  {esquema.metricas.tarjetas.map(t => {
                    const fila = (detalle.metricas ?? []).find(m => m.metrica_id === t.metrica_id)
                    return (
                      <div key={t.id} className="mec-metrica">
                        <span className="mec-metrica__valor">{fila?.valor ?? '—'}</span>
                        <span className="mec-metrica__etiqueta">{t.etiqueta}</span>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="mec-acciones">
                {acciones.filter(a => a.tipo !== 'alta').map(a => (
                  <button key={a.id} className="mec-btn" onClick={() => { setDuplicarDe(null); setAccionAbierta(a) }}>
                    {a.etiqueta}
                  </button>
                ))}
                {/* GEN-GR-002 — «Duplicar» reutiliza la acción de ALTA declarada:
                    abre su formulario con los campos del objeto copiados y la
                    clave vacía. Lo que nace es una propuesta nueva en el estado
                    inicial; el original no se toca. */}
                {acciones.filter(a => a.tipo === 'alta').slice(0, 1).map(a => (
                  <button
                    key={`duplicar-${a.id}`}
                    className="mec-btn"
                    onClick={() => { setDuplicarDe(detalle.objeto ?? {}); setAccionAbierta(a) }}
                  >
                    Duplicar
                  </button>
                ))}
              </div>
            </>
          )}

          {pestana !== 'detalle' && (
            <TablaSimple
              datos={panelDatos}
              vacio="Sin filas."
              columnas={
                pestana === 'versiones'
                  ? ['version', 'es_vigente', 'autor', 'aprobador', 'fecha', 'motivo']
                  : (esquema.registros_ligados.find(r => r.id === pestana)?.columnas ?? [])
              }
              etiquetas={pestana === 'versiones' ? ETIQUETAS_VERSIONES : undefined}
              celda={pestana === 'versiones' ? celdaVersion : undefined}
              filasDe={d => (pestana === 'versiones' ? d.versiones : d.filas) ?? []}
            />
          )}
        </section>
      )}

      {accionAbierta && (
        <FormularioAccion
          key={`${accionAbierta.id}-${duplicarDe ? 'duplicar' : 'normal'}`}
          accion={accionAbierta}
          esquema={esquema}
          objeto={detalle?.objeto}
          clave={clave}
          duplicarDe={duplicarDe}
          onCancelar={() => { setAccionAbierta(null); setDuplicarDe(null) }}
          onEnviar={params => proponer(accionAbierta, params)}
        />
      )}

      {/* La puerta de siempre: se presenta el diff y el usuario aprueba de forma
          explícita. Pórtico no infiere ni presupone la aprobación. */}
      {preview && (
        <div className="mec-preview">
          <ActionPreviewEvent
            payload={preview}
            onApproved={() => { setPreview(null); cargarListado(); if (clave) setClave(clave) }}
          />
          {/* «acción» y no «transición»: desde D-11 la puerta también la produce
              una EDICIÓN del contenido de un objeto en vigor, y llamarla
              transición era falso en la mitad de los casos. El aviso se pinta solo,
              sin ramificar por tipo de acción, porque el kernel ya resuelve la
              puerta y aquí únicamente se muestra.

              GEN-HU-019 (decisión de Jose, 2026-09-28): «quien propone no
              aprueba» dejó de ser cierto — el kernel ya deja aprobar al propio
              proponente si trae el rol. `aprobador_distinto` NO se renombra
              (sigue siendo «esta acción exige el rol de aprobación»); solo
              cambia lo que este aviso AFIRMA, porque «otra persona» ya no es
              un requisito. */}
          {preview.aprobador_distinto && (
            <>
              <p className="mec-aviso">
                Esta acción la aprueba un administrador. Puedes aprobarla tú
                mismo o compartir el enlace para que la apruebe otra persona.
              </p>
              {/* GEN-76·346 (D-1 de Werner) — el enlace compartible: sigue
                  teniendo sentido tras GEN-HU-019 — la autoaprobación no quita
                  que un segundo Owner pueda aprobar en su lugar. */}
              <button
                type="button"
                className="mec-btn mec-btn--enlace"
                onClick={() => copiarEnlace(preview.action_id)}
              >
                <LinkIcon size={14} strokeWidth={1.8} aria-hidden="true" />
                {enlaceCopiado ? 'Enlace copiado' : 'Copiar enlace para que lo apruebe'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function TablaSimple({ datos, columnas, filasDe, vacio, etiquetas = {}, celda, clase }) {
  if (!datos) return <p>Cargando…</p>
  if (datos.error) return <p className="mec-error" role="alert">{datos.error}</p>
  const filas = filasDe(datos)
  if (!filas.length) return <p>{vacio}</p>
  return (
    <table className={clase ? `mec-tabla ${clase}` : 'mec-tabla'}>
      <thead>
        <tr>{columnas.map(c => <th key={c} scope="col">{etiquetas[c] ?? c}</th>)}</tr>
      </thead>
      <tbody>
        {filas.map((f, i) => (
          <tr key={f.registro_id ?? f.version ?? i}>
            {columnas.map(c => <td key={c}>{celda?.(c, f) ?? String(f[c] ?? '—')}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Anfitrión del formulario: borrador en memoria, `motivo` obligatorio y envío
 * como PROPUESTA. Los editores de campo los produce el tipo declarado; el
 * anfitrión no sabe qué significa ninguno.
 */
// GEN-BUG-005 — el botón que confirma nombra la acción elegida. Solo el alta
// (y «Duplicar», que reutiliza el alta) crea una propuesta, y ahí «Proponer»
// es verdad; el resto (transiciones, edición, registros) usa la etiqueta que
// el descriptor declara para esa acción, la misma del botón que abrió el formulario.
export function textoConfirmar(accion) {
  if (accion?.tipo === 'alta') return 'Proponer'
  return accion?.etiqueta?.trim() || 'Proponer'
}

function FormularioAccion({ accion, esquema, objeto, clave, duplicarDe, onCancelar, onEnviar }) {
  const esTransicion = accion.tipo === 'transicion'
  const esRegistro   = accion.tipo === 'registro'
  const registro = esRegistro
    ? (esquema.registros_ligados ?? []).find(r => r.id === accion.registro_ligado)
    : null

  const editables = useMemo(() => {
    if (esTransicion) return []
    if (esRegistro) {
      return (registro?.columnas ?? []).map(c => ({ id: c, etiqueta: c, tipo: 'texto_corto' }))
    }
    const estado = accion.tipo === 'alta' ? esquema.ciclo_de_vida?.inicial : objeto?.estado
    return (esquema.campos ?? []).filter(c => {
      if (c.solo_lectura) return false
      // V-5b — se pregunta por «DECLARADO», no por «no vacío». El centinela de
      // «sin restricción» es la ausencia de la clave; una lista vacía dice «en
      // ningún estado» y aquí se honra como tal (antes concedía en todos). El
      // kernel ya no arranca con un descriptor que la declare, así que este
      // camino no debería darse — pero la pantalla lee lo que el documento dice,
      // no lo contrario.
      if (c.editable_en == null) return true
      return c.editable_en.includes(estado)
    })
  }, [accion, esquema, objeto, registro, esTransicion, esRegistro])

  const [valores, setValores] = useState(() => {
    if (accion.tipo === 'edicion' && objeto) {
      return Object.fromEntries(editables.map(c => [c.id, objeto[c.id]]))
    }
    // GEN-GR-002 — alta rellenada desde otro objeto: se copian los campos
    // editables del alta y la clave del objeto se deja VACÍA (la elige quien
    // propone; es un campo requerido, así que no se puede enviar sin ella).
    if (accion.tipo === 'alta' && duplicarDe) {
      const claveCampo = esquema.objeto?.clave
      return Object.fromEntries(
        editables.map(c => [c.id, c.id === claveCampo ? '' : duplicarDe[c.id]])
      )
    }
    return {}
  })
  const [motivo, setMotivo] = useState('')

  // GEN-HU-008 — qué campos DECLARADOS como `requerido` están vacíos, por id.
  // Mismo criterio que `_params_para` del kernel (V-4: `str(valor).strip()`),
  // sobre el mismo descriptor: cliente y kernel no pueden divergir en qué es
  // «obligatorio» porque los dos lo leen del mismo `esquema.campos`, no de una
  // copia propia. Se calcula ANTES de enviar nada — así el kernel nunca llega
  // a rechazar un alta por esto: la pantalla ya lo sabe.
  const [faltantes, setFaltantes] = useState([])

  const enviar = e => {
    e.preventDefault()
    const faltan = editables
      .filter(c => c.requerido && !String(valores[c.id] ?? '').trim())
      .map(c => c.id)
    if (faltan.length > 0) {
      setFaltantes(faltan)
      return
    }
    setFaltantes([])
    onEnviar(
      esTransicion
        ? { clave, motivo }
        : { clave: accion.tipo === 'alta' ? null : clave, valores, motivo }
    )
  }

  // Ningún campo editable en el estado actual: se dice, y no se deja proponer
  // un cambio vacío. El kernel lo rechazaría igual —él es quien manda— pero un
  // formulario en blanco sin explicación se lee como un fallo de la pantalla.
  const sinCamposEditables = !esTransicion && editables.length === 0

  return (
    <form className="mec-formulario" onSubmit={enviar} aria-label={accion.etiqueta}>
      <h3>{accion.etiqueta}</h3>
      {sinCamposEditables && (
        <p className="mec-aviso" role="status">
          No hay campos editables con el objeto en «{objeto?.estado}».
        </p>
      )}
      {faltantes.length > 0 && (
        <p className="mec-error" role="alert">
          Falta rellenar: {faltantes
            .map(id => editables.find(c => c.id === id)?.etiqueta ?? id)
            .join(', ')}.
        </p>
      )}
      {editables.map(campo => (
        <label
          key={campo.id}
          className={faltantes.includes(campo.id) ? 'mec-campo-edit mec-campo-edit--falta' : 'mec-campo-edit'}
          htmlFor={`campo-${campo.id}`}
        >
          <span>{campo.etiqueta}{campo.requerido ? ' *' : ''}</span>
          <EditorCampo
            campo={campo}
            valor={valores[campo.id]}
            invalido={faltantes.includes(campo.id)}
            onChange={v => {
              setValores(s => ({ ...s, [campo.id]: v }))
              setFaltantes(f => (f.includes(campo.id) ? f.filter(id => id !== campo.id) : f))
            }}
          />
          <AyudaValor campo={campo} valor={valores[campo.id]} />
          {campo.ayuda && <small>{campo.ayuda}</small>}
        </label>
      ))}
      <label className="mec-campo-edit" htmlFor="campo-motivo">
        <span>Motivo *</span>
        <textarea
          id="campo-motivo"
          className="mec-input mec-input--area"
          rows={2}
          value={motivo}
          onChange={e => setMotivo(e.target.value)}
        />
      </label>
      <div className="mec-formulario__pie">
        <button type="button" className="mec-btn" onClick={onCancelar}>Cancelar</button>
        <button
          type="submit"
          className="mec-btn mec-btn--primary"
          disabled={!motivo.trim() || sinCamposEditables}
        >
          {textoConfirmar(accion)}
        </button>
      </div>
    </form>
  )
}
